/**
 * Detail panels for leagues and events. A link to #league/<id> or
 * #event/<id> opens a dialog over the current page, so every panel has an
 * address that can be shared, and the back button closes it. Panels link to
 * each other: an event to its league, a league to its events.
 *
 * League records come from records.ts. Events are the rows of the events
 * table: read in place on /events/, fetched from it everywhere else.
 */
import { columnIndex, dayFmt, fetchEventsTable, parseDates, tagEvents, today, type Status } from "./calendar";
import { flagColumn, flagFor } from "./flags";
import { MEDIA } from "./media";
import { EVENT_NOTES, LEAGUES, type League } from "./records";
import { channelRow, pictureFigure, videoGrid } from "./watch";

const EVENTS_URL = "/events/";
const HASH = /^#(league|event)\/([a-z0-9-]+)$/;

interface EventInfo {
  id: string;
  name: string;
  /** Our write-up, when the table links one. */
  page?: string;
  label: string;
  start: Date | null;
  end: Date | null;
  status: Status;
  format: string;
  where: string;
  result: string;
  source: Node[];
  league?: League;
}

const leagueById = new Map(LEAGUES.map((l) => [l.id, l]));

function leagueFor(name: string): League | undefined {
  return LEAGUES.find((l) => l.match?.test(name));
}

// ---------------------------------------------------------------------------
// Reading events

function readEvents(table: HTMLTableElement): EventInfo[] {
  tagEvents(table);
  const col = (name: string) => columnIndex(table, name);
  const [eventCol, formatCol, whereCol, resultCol, sourceCol] = ["event", "format", "where", "result", "source"].map(col);
  const now = today();

  return Array.from(table.tBodies[0]?.rows ?? []).map((row) => {
    const text = (i: number) => (i < 0 ? "" : row.cells[i]?.textContent?.trim() ?? "");
    const name = text(eventCol);
    const parsed = parseDates(row.dataset.dates ?? "");
    let status: Status = "upcoming";
    if (parsed && parsed.end < now) status = "past";
    else if (parsed && parsed.start <= now) status = "live";
    const own = row.cells[eventCol]?.querySelector<HTMLAnchorElement>('a[href^="/"]');
    return {
      id: row.dataset.eventId!,
      name,
      page: own?.getAttribute("href") ?? undefined,
      label: parsed?.label ?? (row.dataset.dates || "Date to be announced"),
      start: parsed?.start ?? null,
      end: parsed?.end ?? null,
      status,
      format: text(formatCol),
      where: text(whereCol),
      result: text(resultCol),
      source: sourceCol < 0 ? [] : Array.from(row.cells[sourceCol]?.childNodes ?? []).map((n) => n.cloneNode(true)),
      league: leagueFor(name),
    };
  });
}

let eventsPromise: Promise<EventInfo[]> | null = null;
/** The events once read, so a panel can be drawn without waiting. */
let eventsReady: EventInfo[] | null = null;

/** The events tables on this page: only /events/ has them. */
function localTables(): HTMLTableElement[] {
  return Array.from(document.querySelectorAll<HTMLTableElement>("[data-calendar] table"));
}

/**
 * Every event, read once. On /events/ the rows are read before the calendar
 * reorders them; elsewhere the page is fetched (shared with the homepage
 * strip), and a failed fetch leaves league panels without their event lists
 * rather than breaking them.
 */
function loadEvents(): Promise<EventInfo[]> {
  if (!eventsPromise) {
    // The calendar splits /events/ into an upcoming and a past table.
    const local = localTables();
    eventsPromise = (local.length ? Promise.resolve(local.flatMap(readEvents)) : fetchEventsTable(EVENTS_URL).then((t) => (t ? readEvents(t) : []))).then(
      (events) => (eventsReady = events),
    );
  }
  return eventsPromise;
}

/** Upcoming soonest first, then past most recent first. */
function byWhen(events: EventInfo[]): { upcoming: EventInfo[]; past: EventInfo[] } {
  const upcoming = events
    .filter((e) => e.status !== "past")
    .sort((a, b) => (a.start ? +a.start : Infinity) - (b.start ? +b.start : Infinity));
  const past = events.filter((e) => e.status === "past").sort((a, b) => +b.end! - +a.end!);
  return { upcoming, past };
}

// ---------------------------------------------------------------------------
// Building blocks

type Child = Node | string | null | undefined | false;

function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  for (const c of children) if (c) el.append(c);
  return el;
}

/** Drops the empty values h() would skip, for DOM methods that take no nulls. */
function present(...children: Child[]): (Node | string)[] {
  return children.filter((c): c is Node | string => Boolean(c));
}

/** A place with its country's flag in front, when we have one. */
function place(text: string): DocumentFragment | null {
  if (!text) return null;
  const out = document.createDocumentFragment();
  out.append(...present(flagFor(text), text));
  return out;
}

function external(href: string, label: string): HTMLAnchorElement {
  return h("a", { href, rel: "noopener", target: "_blank" }, label);
}

function facts(rows: [string, Child | Child[]][]): HTMLDListElement {
  const dl = h("dl", { class: "facts" });
  for (const [label, value] of rows) {
    const values = Array.isArray(value) ? value : [value];
    if (!values.some(Boolean)) continue;
    dl.append(h("dt", {}, label), h("dd", {}, ...values));
  }
  return dl;
}

function statusBadge(e: EventInfo): HTMLElement | null {
  if (e.status === "live") return h("span", { class: "badge badge--live" }, "On now");
  if (e.status === "upcoming") return h("span", { class: "badge badge--soon" }, "Upcoming");
  return null;
}

function eventList(events: EventInfo[]): HTMLElement {
  return h(
    "ul",
    { class: "entity-list" },
    ...events.map((e) =>
      h(
        "li",
        {},
        h("span", { class: "entity-list__when" }, e.label),
        h("a", { href: `#event/${e.id}` }, e.name),
        e.where ? h("span", { class: "entity-list__meta muted" }, place(e.where)) : null,
      ),
    ),
  );
}

/** The league's events, split into what is coming and what has happened. */
function leagueEvents(league: League, events: EventInfo[], pastLimit = 6): DocumentFragment {
  const own = events.filter((e) => e.league === league);
  const { upcoming, past } = byWhen(own);
  const out = document.createDocumentFragment();
  if (!own.length) {
    out.append(h("p", { class: "muted" }, "No dated events for this league yet."));
    return out;
  }
  if (upcoming.length) out.append(h("h3", {}, "Coming up"), eventList(upcoming));
  if (past.length) {
    out.append(h("h3", {}, "Recent"), eventList(past.slice(0, pastLimit)));
    if (past.length > pastLimit) out.append(h("p", { class: "muted" }, `And ${past.length - pastLimit} earlier, on the `, h("a", { href: EVENTS_URL }, "events page"), "."));
  }
  return out;
}

function leagueFacts(league: League): HTMLDListElement {
  return facts([
    ["Format", league.format],
    ["Based in", place(league.base)],
    ["Runs", league.runs],
    ...(league.facts ?? []),
  ]);
}

function sourceList(sources: string[] | undefined): HTMLElement | null {
  if (!sources?.length) return null;
  return h(
    "p",
    { class: "entity-sources muted" },
    "Sources: ",
    ...sources.flatMap((url, i) => [i ? ", " : "", external(url, new URL(url).hostname.replace(/^www\./, ""))]),
  );
}

// ---------------------------------------------------------------------------
// Panels

/**
 * A league panel. Without the events yet (null), it opens straight away with
 * a placeholder, and the list is filled in when they arrive.
 */
function leaguePanel(league: League, events: EventInfo[] | null): HTMLElement {
  const links = h(
    "p",
    { class: "entity-actions" },
    league.page ? h("a", { class: "btn btn--sm", href: league.page }, "Full profile") : null,
    league.website ? external(league.website, league.websiteLabel ?? "Website") : null,
  );
  const media = MEDIA[league.id];
  const eventSection = h("section", { class: "entity-section" });
  if (events) eventSection.append(leagueEvents(league, events));
  else {
    eventSection.append(h("p", { class: "muted" }, "Loading events..."));
    void loadEvents().then((all) => eventSection.replaceChildren(leagueEvents(league, all)));
  }
  return h(
    "div",
    {},
    media?.picture ? pictureFigure(media.picture, "media-figure entity-hero", true) : null,
    h("p", { class: "eyebrow" }, "League"),
    h("h2", { id: "entity-title" }, league.name),
    league.summary ? h("p", { class: "entity-summary" }, league.summary) : null,
    leagueFacts(league),
    links.childElementCount ? links : null,
    channelRow(media?.channels),
    media?.videos?.length ? h("section", { class: "entity-section" }, h("h3", {}, "Watch"), videoGrid(media.videos.slice(0, 3))) : null,
    eventSection,
    sourceList(league.sources),
  );
}

function eventPanel(e: EventInfo, events: EventInfo[]): HTMLElement {
  const note = EVENT_NOTES[e.id];
  const more = e.league
    ? byWhen(events.filter((o) => o.league === e.league && o !== e))
    : { upcoming: [], past: [] };
  const siblings = [...more.upcoming.slice(0, 3), ...more.past.slice(0, 3)];
  return h(
    "div",
    {},
    h("p", { class: "eyebrow" }, "Event ", statusBadge(e)),
    h("h2", { id: "entity-title" }, e.name),
    note ? h("p", { class: "entity-summary" }, note.summary) : null,
    facts([
      ["When", e.label],
      ["Where", place(e.where)],
      ["Format", e.format],
      ["Result", e.result],
      ["League", e.league ? h("a", { href: `#league/${e.league.id}` }, e.league.name) : null],
      ["Source", e.source.length ? e.source.map((n) => n.cloneNode(true)) : null],
    ]),
    e.page ? h("p", { class: "entity-actions" }, h("a", { class: "btn btn--sm", href: e.page }, "Read our write-up")) : null,
    e.league && siblings.length
      ? h("section", { class: "entity-section" }, h("h3", {}, `More from ${e.league.name}`), eventList(siblings))
      : null,
  );
}

// ---------------------------------------------------------------------------
// The dialog and its history

let dialog: HTMLDialogElement | null = null;
let body: HTMLElement;
/** How many history entries the open panels have pushed. */
let depth = 0;

function ensureDialog(): HTMLDialogElement {
  if (dialog) return dialog;
  body = h("div", { class: "entity__body" });
  const close = h("button", { type: "button", class: "entity__close", "aria-label": "Close" }, "×");
  dialog = h("dialog", { class: "entity", "aria-labelledby": "entity-title" }, close, body);
  close.addEventListener("click", () => dialog!.close());
  // A click on the backdrop lands on the dialog itself.
  dialog.addEventListener("click", (ev) => {
    if (ev.target === dialog) dialog!.close();
  });
  // Closed by the button, Escape or the backdrop: take the panel's history
  // entries off too. Closed by the back button: the hash has already gone.
  dialog.addEventListener("close", () => {
    if (!HASH.test(location.hash)) return;
    if (depth > 0) {
      const back = depth;
      depth = 0;
      history.go(-back);
    } else {
      history.replaceState(history.state, "", location.pathname + location.search);
    }
  });
  document.body.append(dialog);
  return dialog;
}

function closeQuietly(): void {
  if (dialog?.open) dialog.close();
}

async function show(hash: string): Promise<void> {
  const m = HASH.exec(hash);
  if (!m) {
    closeQuietly();
    return;
  }
  const [, kind, id] = m;
  let panel: HTMLElement | null = null;
  if (kind === "league") {
    // League panels open at once; their events follow.
    const league = leagueById.get(id);
    if (league) panel = leaguePanel(league, eventsReady);
  } else {
    // An event panel is all event data, so it waits. Event links mostly sit in
    // places that loaded the events already, so this is rarely a real wait.
    const events = eventsReady ?? (await loadEvents());
    if (location.hash !== hash) return;
    const e = events.find((x) => x.id === id);
    if (e) panel = eventPanel(e, events);
  }
  if (!panel) {
    closeQuietly();
    return;
  }
  const d = ensureDialog();
  body.replaceChildren(panel);
  d.scrollTop = 0;
  if (!d.open) d.showModal();
}

function initRouting(): void {
  document.addEventListener("click", (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    const a = (ev.target as Element).closest<HTMLAnchorElement>("a[href^='#league/'], a[href^='#event/']");
    if (!a) return;
    ev.preventDefault();
    const hash = a.getAttribute("href")!;
    depth++;
    history.pushState({ entityDepth: depth }, "", hash);
    void show(hash);
  });
  window.addEventListener("popstate", (ev) => {
    depth = (ev.state as { entityDepth?: number } | null)?.entityDepth ?? 0;
    void show(location.hash);
  });
  // A reload keeps the history entries the panels pushed.
  depth = (history.state as { entityDepth?: number } | null)?.entityDepth ?? 0;
  if (HASH.test(location.hash)) void show(location.hash);
}

// ---------------------------------------------------------------------------
// Links into the panels

/** Event names in the events tables open their panel. */
function linkEventRows(): void {
  for (const table of document.querySelectorAll<HTMLTableElement>("[data-calendar] table")) {
    const eventCol = columnIndex(table, "event");
    if (eventCol < 0) continue;
    for (const row of Array.from(table.tBodies[0]?.rows ?? [])) {
      const cell = row.cells[eventCol];
      if (!cell || !row.dataset.eventId) continue;
      const link = h("a", { class: "entity-link", href: `#event/${row.dataset.eventId}` }, cell.textContent?.trim() ?? "");
      cell.replaceChildren(link);
    }
  }
}

/** League names in the leagues directory open their panel. */
function linkLeagueRows(): void {
  const byName = new Map(LEAGUES.map((l) => [l.name, l]));
  for (const table of document.querySelectorAll<HTMLTableElement>("[data-leagues] table")) {
    for (const row of Array.from(table.tBodies[0]?.rows ?? [])) {
      const cell = row.cells[0];
      const league = byName.get(cell?.textContent?.trim() ?? "");
      if (!cell || !league) continue;
      cell.replaceChildren(h("a", { class: "entity-link", href: `#league/${league.id}` }, league.name));
    }
  }
}

/**
 * On a league profile or an event write-up, adds what the records know: the
 * league's picture, channels, videos and events, or the event's league and its
 * other events. A profile's facts, where to watch and sources are written in
 * its markdown, so crawlers and readers without JavaScript get them; this
 * adds only what changes with the reader's date or comes from src/media.ts.
 */
async function enrichProfile(): Promise<void> {
  const mount = document.querySelector<HTMLElement>("[data-entity-mount]");
  if (!mount) return;
  const path = location.pathname;
  const league = LEAGUES.find((l) => l.page === path);
  const events = await loadEvents();

  if (league) {
    const media = MEDIA[league.id];
    mount.replaceChildren(
      ...present(
        media?.picture ? pictureFigure(media.picture) : null,
        channelRow(media?.channels),
        media?.videos?.length ? h("h2", {}, "Latest videos") : null,
        media?.videos?.length ? videoGrid(media.videos) : null,
        h("h2", {}, "Events"),
        leagueEvents(league, events, 10),
      ),
    );
    return;
  }

  const e = events.find((x) => x.page === path);
  if (!e?.league) return;
  const others = byWhen(events.filter((o) => o.league === e.league && o !== e));
  mount.replaceChildren(
    ...present(
      h("h2", {}, `Part of ${e.league.name}`),
      h("p", {}, e.league.summary ?? e.league.runs, " ", h("a", { href: `#league/${e.league.id}` }, `About ${e.league.name}`)),
      others.upcoming.length || others.past.length ? eventList([...others.upcoming.slice(0, 4), ...others.past.slice(0, 4)]) : null,
    ),
  );
}

/** Call after initCalendar, so the rows are already in their tables. */
export function initEntities(): void {
  // On /events/, read the events before linkEventRows replaces the links to
  // our write-ups. Elsewhere, fetch /events/ only once the reader heads for a
  // panel link, rather than on every page view.
  if (localTables().length) void loadEvents();
  else {
    const warm = (ev: Event) => {
      if (!(ev.target as Element).closest?.("a[href^='#league/'], a[href^='#event/']")) return;
      void loadEvents();
      for (const type of ["pointerover", "focusin", "touchstart"]) document.removeEventListener(type, warm);
    };
    for (const type of ["pointerover", "focusin", "touchstart"]) document.addEventListener(type, warm, { passive: true });
  }
  linkEventRows();
  linkLeagueRows();
  flagColumn("[data-calendar]", "where");
  flagColumn("[data-leagues]", "base");
  initRouting();
  void enrichProfile();
}
